/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils-define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name altcase
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/altcase}
*/
import altcase from '@stdlib/string-base-altcase';
setReadOnly( ns, 'altcase', altcase );

/**
* @name atob
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/atob}
*/
import atob from '@stdlib/string-base-atob';
setReadOnly( ns, 'atob', atob );

/**
* @name base64ToUint8Array
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/base64-to-uint8array}
*/
import base64ToUint8Array from '@stdlib/string-base-base64-to-uint8array';
setReadOnly( ns, 'base64ToUint8Array', base64ToUint8Array );

/**
* @name camelcase
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/camelcase}
*/
import camelcase from '@stdlib/string-base-camelcase';
setReadOnly( ns, 'camelcase', camelcase );

/**
* @name capitalize
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/capitalize}
*/
import capitalize from '@stdlib/string-base-capitalize';
setReadOnly( ns, 'capitalize', capitalize );

/**
* @name codePointAt
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/code-point-at}
*/
import codePointAt from '@stdlib/string-base-code-point-at';
setReadOnly( ns, 'codePointAt', codePointAt );

/**
* @name constantcase
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/constantcase}
*/
import constantcase from '@stdlib/string-base-constantcase';
setReadOnly( ns, 'constantcase', constantcase );

/**
* @name distances
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/string/base/distances}
*/
import distances from '@stdlib/string-base-distances';
setReadOnly( ns, 'distances', distances );

/**
* @name dotcase
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/dotcase}
*/
import dotcase from '@stdlib/string-base-dotcase';
setReadOnly( ns, 'dotcase', dotcase );

/**
* @name endsWith
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/ends-with}
*/
import endsWith from '@stdlib/string-base-ends-with';
setReadOnly( ns, 'endsWith', endsWith );

/**
* @name first
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/first}
*/
import first from '@stdlib/string-base-first';
setReadOnly( ns, 'first', first );

/**
* @name firstCodePoint
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/first-code-point}
*/
import firstCodePoint from '@stdlib/string-base-first-code-point';
setReadOnly( ns, 'firstCodePoint', firstCodePoint );

/**
* @name firstGraphemeCluster
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/first-grapheme-cluster}
*/
import firstGraphemeCluster from '@stdlib/string-base-first-grapheme-cluster';
setReadOnly( ns, 'firstGraphemeCluster', firstGraphemeCluster );

/**
* @name forEach
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/for-each}
*/
import forEach from '@stdlib/string-base-for-each';
setReadOnly( ns, 'forEach', forEach );

/**
* @name forEachCodePoint
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/for-each-code-point}
*/
import forEachCodePoint from '@stdlib/string-base-for-each-code-point';
setReadOnly( ns, 'forEachCodePoint', forEachCodePoint );

/**
* @name forEachCodePointRight
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/for-each-code-point-right}
*/
import forEachCodePointRight from '@stdlib/string-base-for-each-code-point-right';
setReadOnly( ns, 'forEachCodePointRight', forEachCodePointRight );

/**
* @name forEachGraphemeCluster
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/for-each-grapheme-cluster}
*/
import forEachGraphemeCluster from '@stdlib/string-base-for-each-grapheme-cluster';
setReadOnly( ns, 'forEachGraphemeCluster', forEachGraphemeCluster );

/**
* @name forEachRight
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/for-each-right}
*/
import forEachRight from '@stdlib/string-base-for-each-right';
setReadOnly( ns, 'forEachRight', forEachRight );

/**
* @name formatInterpolate
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/format-interpolate}
*/
import formatInterpolate from '@stdlib/string-base-format-interpolate';
setReadOnly( ns, 'formatInterpolate', formatInterpolate );

/**
* @name formatTokenize
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/format-tokenize}
*/
import formatTokenize from '@stdlib/string-base-format-tokenize';
setReadOnly( ns, 'formatTokenize', formatTokenize );

/**
* @name headercase
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/headercase}
*/
import headercase from '@stdlib/string-base-headercase';
setReadOnly( ns, 'headercase', headercase );

/**
* @name invcase
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/invcase}
*/
import invcase from '@stdlib/string-base-invcase';
setReadOnly( ns, 'invcase', invcase );

/**
* @name kebabcase
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/kebabcase}
*/
import kebabcase from '@stdlib/string-base-kebabcase';
setReadOnly( ns, 'kebabcase', kebabcase );

/**
* @name last
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/last}
*/
import last from '@stdlib/string-base-last';
setReadOnly( ns, 'last', last );

/**
* @name lastCodePoint
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/last-code-point}
*/
import lastCodePoint from '@stdlib/string-base-last-code-point';
setReadOnly( ns, 'lastCodePoint', lastCodePoint );

/**
* @name lastGraphemeCluster
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/last-grapheme-cluster}
*/
import lastGraphemeCluster from '@stdlib/string-base-last-grapheme-cluster';
setReadOnly( ns, 'lastGraphemeCluster', lastGraphemeCluster );

/**
* @name lpad
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/left-pad}
*/
import lpad from '@stdlib/string-base-left-pad';
setReadOnly( ns, 'lpad', lpad );

/**
* @name ltrim
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/left-trim}
*/
import ltrim from '@stdlib/string-base-left-trim';
setReadOnly( ns, 'ltrim', ltrim );

/**
* @name lowercase
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/lowercase}
*/
import lowercase from '@stdlib/string-base-lowercase';
setReadOnly( ns, 'lowercase', lowercase );

/**
* @name pascalcase
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/pascalcase}
*/
import pascalcase from '@stdlib/string-base-pascalcase';
setReadOnly( ns, 'pascalcase', pascalcase );

/**
* @name percentEncode
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/percent-encode}
*/
import percentEncode from '@stdlib/string-base-percent-encode';
setReadOnly( ns, 'percentEncode', percentEncode );

/**
* @name removeFirst
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/remove-first}
*/
import removeFirst from '@stdlib/string-base-remove-first';
setReadOnly( ns, 'removeFirst', removeFirst );

/**
* @name removeFirstCodePoint
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/remove-first-code-point}
*/
import removeFirstCodePoint from '@stdlib/string-base-remove-first-code-point';
setReadOnly( ns, 'removeFirstCodePoint', removeFirstCodePoint );

/**
* @name removeFirstGraphemeCluster
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/remove-first-grapheme-cluster}
*/
import removeFirstGraphemeCluster from '@stdlib/string-base-remove-first-grapheme-cluster';
setReadOnly( ns, 'removeFirstGraphemeCluster', removeFirstGraphemeCluster );

/**
* @name removeLast
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/remove-last}
*/
import removeLast from '@stdlib/string-base-remove-last';
setReadOnly( ns, 'removeLast', removeLast );

/**
* @name removeLastCodePoint
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/remove-last-code-point}
*/
import removeLastCodePoint from '@stdlib/string-base-remove-last-code-point';
setReadOnly( ns, 'removeLastCodePoint', removeLastCodePoint );

/**
* @name removeLastGraphemeCluster
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/remove-last-grapheme-cluster}
*/
import removeLastGraphemeCluster from '@stdlib/string-base-remove-last-grapheme-cluster';
setReadOnly( ns, 'removeLastGraphemeCluster', removeLastGraphemeCluster );

/**
* @name repeat
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/repeat}
*/
import repeat from '@stdlib/string-base-repeat';
setReadOnly( ns, 'repeat', repeat );

/**
* @name replace
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/replace}
*/
import replace from '@stdlib/string-base-replace';
setReadOnly( ns, 'replace', replace );

/**
* @name replaceAfter
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/replace-after}
*/
import replaceAfter from '@stdlib/string-base-replace-after';
setReadOnly( ns, 'replaceAfter', replaceAfter );

/**
* @name replaceAfterLast
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/replace-after-last}
*/
import replaceAfterLast from '@stdlib/string-base-replace-after-last';
setReadOnly( ns, 'replaceAfterLast', replaceAfterLast );

/**
* @name replaceBefore
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/replace-before}
*/
import replaceBefore from '@stdlib/string-base-replace-before';
setReadOnly( ns, 'replaceBefore', replaceBefore );

/**
* @name replaceBeforeLast
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/replace-before-last}
*/
import replaceBeforeLast from '@stdlib/string-base-replace-before-last';
setReadOnly( ns, 'replaceBeforeLast', replaceBeforeLast );

/**
* @name reverse
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/reverse}
*/
import reverse from '@stdlib/string-base-reverse';
setReadOnly( ns, 'reverse', reverse );

/**
* @name reverseCodePoints
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/reverse-code-points}
*/
import reverseCodePoints from '@stdlib/string-base-reverse-code-points';
setReadOnly( ns, 'reverseCodePoints', reverseCodePoints );

/**
* @name reverseGraphemeClusters
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/reverse-grapheme-clusters}
*/
import reverseGraphemeClusters from '@stdlib/string-base-reverse-grapheme-clusters';
setReadOnly( ns, 'reverseGraphemeClusters', reverseGraphemeClusters );

/**
* @name rpad
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/right-pad}
*/
import rpad from '@stdlib/string-base-right-pad';
setReadOnly( ns, 'rpad', rpad );

/**
* @name rtrim
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/right-trim}
*/
import rtrim from '@stdlib/string-base-right-trim';
setReadOnly( ns, 'rtrim', rtrim );

/**
* @name slice
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/slice}
*/
import slice from '@stdlib/string-base-slice';
setReadOnly( ns, 'slice', slice );

/**
* @name sliceCodePoints
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/slice-code-points}
*/
import sliceCodePoints from '@stdlib/string-base-slice-code-points';
setReadOnly( ns, 'sliceCodePoints', sliceCodePoints );

/**
* @name sliceGraphemeClusters
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/slice-grapheme-clusters}
*/
import sliceGraphemeClusters from '@stdlib/string-base-slice-grapheme-clusters';
setReadOnly( ns, 'sliceGraphemeClusters', sliceGraphemeClusters );

/**
* @name snakecase
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/snakecase}
*/
import snakecase from '@stdlib/string-base-snakecase';
setReadOnly( ns, 'snakecase', snakecase );

/**
* @name startcase
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/startcase}
*/
import startcase from '@stdlib/string-base-startcase';
setReadOnly( ns, 'startcase', startcase );

/**
* @name startsWith
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/starts-with}
*/
import startsWith from '@stdlib/string-base-starts-with';
setReadOnly( ns, 'startsWith', startsWith );

/**
* @name stickycase
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/stickycase}
*/
import stickycase from '@stdlib/string-base-stickycase';
setReadOnly( ns, 'stickycase', stickycase );

/**
* @name trim
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/trim}
*/
import trim from '@stdlib/string-base-trim';
setReadOnly( ns, 'trim', trim );

/**
* @name truncateMiddle
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/truncate-middle}
*/
import truncateMiddle from '@stdlib/string-base-truncate-middle';
setReadOnly( ns, 'truncateMiddle', truncateMiddle );

/**
* @name uncapitalize
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/uncapitalize}
*/
import uncapitalize from '@stdlib/string-base-uncapitalize';
setReadOnly( ns, 'uncapitalize', uncapitalize );

/**
* @name uppercase
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base/uppercase}
*/
import uppercase from '@stdlib/string-base-uppercase';
setReadOnly( ns, 'uppercase', uppercase );


// EXPORTS //

export default ns;
